/*
 * Copyright (c) 2022-2024 Antmicro <www.antmicro.com>
 *
 * SPDX-License-Identifier: Apache-2.0
 */
/*
The baklavajs functionality was fully copied to this file, as the original
implementation is not exported and cannot be integrated with custom history
as the Commands have been replaced.

The original source is slightly adjusted to integrate with the customized history
and the new command handler
*/
import { computed, reactive, ref, } from 'vue';
import { v4 as uuidv4 } from 'uuid';
import { GRAPH_NODE_TYPE_PREFIX, } from '@baklavajs/core';
import { useViewModel, } from '@baklavajs/renderer-vue';
import { startTransaction, commitTransaction, } from './History.ts';
import { removeNode } from '../custom/CustomNode.js';
export const COPY_COMMAND = 'COPY';
export const DELETE_COMMAND = 'DELETE';
export const UNHIGHLIGHT_COMMAND = 'UNHIGHLIGHT';
export const PASTE_COMMAND = 'PASTE';
export const CLEAR_CLIPBOARD_COMMAND = 'CLEAR_CLIPBOARD';
export function useClipboard(displayedGraph, editor, commandHandler) {
    const token = Symbol('ClipboardToken');
    const nodeBuffer = ref('');
    const connectionBuffer = ref('');
    const consecutivePasteNumber = ref(0);
    const isEmpty = computed(() => !nodeBuffer.value);
    const clear = () => {
        nodeBuffer.value = '';
        connectionBuffer.value = '';
    };
    const copy = () => {
        // find all connections from and to the selected nodes
        const interfacesOfSelectedNodes = displayedGraph.value.selectedNodes.flatMap((n) => [
            ...Object.values(n.inputs),
            ...Object.values(n.outputs),
        ]);
        const connections = displayedGraph.value.connections
            .filter((conn) => interfacesOfSelectedNodes.includes(conn.from) ||
            interfacesOfSelectedNodes.includes(conn.to))
            .map((conn) => ({ from: conn.from.id, to: conn.to.id }));
        connectionBuffer.value = JSON.stringify(connections);
        nodeBuffer.value = JSON.stringify(displayedGraph.value.selectedNodes.map((n) => n.save()));
        consecutivePasteNumber.value = 0;
    };
    const del = () => {
        const { viewModel } = useViewModel();
        if (viewModel.value.editor.readonly)
            return;
        startTransaction();
        displayedGraph.value.selectedNodes.forEach((node) => {
            removeNode(node);
        });
        commitTransaction();
    };
    const findInterface = (nodes, id, io) => {
        for (let i = 0; i < nodes.length; i += 1) {
            let intf;
            if (!io || io === 'input') {
                intf = Object.values(nodes[i].inputs).find((iface) => iface.id === id);
            }
            if (!intf && (!io || io === 'output')) {
                intf = Object.values(nodes[i].outputs).find((iface) => iface.id === id);
            }
            if (intf) {
                return intf;
            }
        }
        return undefined;
    };
    const paste = () => {
        var _a, _b;
        const { viewModel } = useViewModel();
        if (isEmpty.value || viewModel.value.editor.readonly) {
            return;
        }
        const movementStep = (_a = viewModel.value.movementStep) !== null && _a !== void 0 ? _a : 1;
        // Map old IDs to new IDs
        const idmap = new Map();
        const parsedNodeBuffer = JSON.parse(nodeBuffer.value);
        const parsedConnectionBuffer = JSON.parse(connectionBuffer.value);
        const newNodes = [];
        const newConnections = [];
        const graph = displayedGraph.value;
        commandHandler.executeCommand('START_TRANSACTION');
        for (let i = 0; i < parsedNodeBuffer.length; i += 1) {
            let nodeType;
            if (((_b = parsedNodeBuffer[i]) === null || _b === void 0 ? void 0 : _b.subgraph) !== undefined) {
                nodeType = editor.value.nodeTypes.get(`${GRAPH_NODE_TYPE_PREFIX}${parsedNodeBuffer[i].name}`);
            }
            else {
                nodeType = editor.value.nodeTypes.get(parsedNodeBuffer[i].name);
            }
            if (!nodeType) {
                return;
            }
            /* eslint-disable-next-line new-cap */
            let copiedNode = new nodeType.type();
            newNodes.push(copiedNode);
            copiedNode.hooks.beforeLoad.subscribe(token, (nodeState) => {
                const ns = nodeState;
                if (ns.position) {
                    consecutivePasteNumber.value += 1;
                    ns.position.x += consecutivePasteNumber.value * Math.max(40, movementStep);
                    ns.position.y += consecutivePasteNumber.value * Math.max(40, movementStep);
                }
                if (parsedNodeBuffer[i].graphState !== undefined) {
                    parsedNodeBuffer[i].graphState.nodes.forEach((n) => {
                        /* eslint-disable-next-line no-param-reassign */
                        if (n.type !== undefined)
                            n.name = n.type;
                    });
                }
                copiedNode.hooks.beforeLoad.unsubscribe(token);
                return ns;
            });
            copiedNode = graph.addNode(copiedNode);
            const mapNewId = (obj) => {
                /* eslint-disable no-param-reassign */
                const newId = uuidv4();
                idmap.set(obj.id, newId);
                obj.id = newId;
            };
            const assignNewIds = (node) => {
                /* eslint-disable no-param-reassign */
                // New node id
                mapNewId(node);
                if (node.graphState !== undefined) {
                    mapNewId(node.graphState);
                    node.graphState.nodes.forEach((subNode) => {
                        assignNewIds(subNode);
                    });
                    // If it is a subgraph node, then some interfaces have to have the same IDs
                    // as the ones in the subgraph
                    node.interfaces.forEach((intf) => {
                        var _a;
                        intf.id = (_a = idmap.get(intf.id)) !== null && _a !== void 0 ? _a : intf.id;
                        // If the node has any external interfaces, then their names have
                        // to be resolved as they cannot conflict with the existing ones.
                        if (intf.externalName !== undefined) {
                            intf.externalName = graph.resolveNewExposedName(intf.externalName);
                        }
                    });
                    node.graphState.connections.forEach((conn) => {
                        if (idmap.get(conn.from) === undefined ||
                            idmap.get(conn.to) === undefined) {
                            throw new Error('Error when executing copy and paste. ' +
                                `Connection from interface ${conn.from} to ${conn.to} is invalid`);
                        }
                        conn.from = idmap.get(conn.from);
                        conn.to = idmap.get(conn.to);
                    });
                }
                else {
                    // If it is a regular node, then interfaces need new IDs.
                    node.interfaces.forEach((intf) => {
                        mapNewId(intf);
                        // If the node has any external interfaces, then their names have
                        // to be resolved as they cannot conflict with the existing ones.
                        if (intf.externalName !== undefined) {
                            intf.externalName = graph.resolveNewExposedName(intf.externalName);
                        }
                    });
                }
            };
            assignNewIds(parsedNodeBuffer[i]);
            copiedNode.load(Object.assign(Object.assign({}, parsedNodeBuffer[i]), { id: copiedNode.id }));
            // If the pasted graph was inside of a graph node, then the graph node has to
            // have its exposed interfaces refreshed
            if (displayedGraph.value.graphNode !== undefined) {
                displayedGraph.value.graphNode.updateExposedInterfaces();
            }
        }
        for (let i = 0; i < parsedConnectionBuffer.length; i += 1) {
            const fromId = idmap.get(parsedConnectionBuffer[i].from);
            const toId = idmap.get(parsedConnectionBuffer[i].to);
            if (fromId && toId) {
                const fromIntf = findInterface(newNodes, fromId, 'output');
                const toIntf = findInterface(newNodes, toId, 'input');
                if (fromIntf && toIntf) {
                    const newConnection = graph.addConnection(fromIntf, toIntf);
                    if (newConnection) {
                        newConnections.push(newConnection);
                    }
                }
            }
        }
        commandHandler.executeCommand('COMMIT_TRANSACTION');
        /* eslint-disable-next-line consistent-return */
        return {
            newNodes,
            newConnections,
        };
    };
    commandHandler.registerCommand(DELETE_COMMAND, {
        canExecute: () => displayedGraph.value.selectedNodes.length > 0,
        execute: del,
    });
    commandHandler.registerHotkey(['Delete'], DELETE_COMMAND);
    commandHandler.registerCommand(UNHIGHLIGHT_COMMAND, {
        canExecute: () => true,
        execute: () => {
            displayedGraph.value.selectedNodes = []; // eslint-disable-line no-param-reassign
        },
    });
    commandHandler.registerHotkey(['Escape'], UNHIGHLIGHT_COMMAND);
    commandHandler.registerCommand(COPY_COMMAND, {
        canExecute: () => true,
        execute: copy,
    });
    commandHandler.registerHotkey(['Control', 'c'], COPY_COMMAND);
    commandHandler.registerCommand(PASTE_COMMAND, {
        canExecute: () => !isEmpty.value,
        execute: paste,
    });
    commandHandler.registerHotkey(['Control', 'v'], PASTE_COMMAND);
    commandHandler.registerCommand(CLEAR_CLIPBOARD_COMMAND, {
        canExecute: () => true,
        execute: clear,
    });
    return reactive({ isEmpty });
}
